/**
 * /admin/reports/group-summary — Group Summary.
 * @file src/web/features/admin/pages/reports/group-summary.tsx
 *
 * Net closing balance rolled up by account group (as at the period end).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ListTree } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';

interface Row { name: string; nature: string; count: number; debit: number; credit: number }
interface GS { rows: Row[]; totals: { debit: number; credit: number; balanced: boolean } }

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const NATURE: Record<string, string> = { asset: 'bg-info-50 text-info-600', liability: 'bg-highlighter-40 text-inkblack', income: 'bg-ok-50 text-ok-600', expense: 'bg-warn-50 text-warn-600', unknown: 'bg-ink-100 text-ink-500' };

export default function GroupSummaryPage() {
  const { entityId } = useCompany();
  const { to } = usePeriod();
  const [data, setData] = useState<GS | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false; setData(null); setError(null);
    adminFetchJson<GS>('/api/tally-import/group-summary', { query: { entityId: entityId ?? undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [entityId, to]);

  return (
    <>
      <SEO title="Group Summary" />
      <AdminPageHeader title="Group Summary" breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: 'Group Summary' }]} />
      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700"><ChevronLeft className="h-4 w-4" /> All reports</Link>
      <PeriodBar />

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label="Building group summary" />}

      {data && data.rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><ListTree className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">Nothing to show</h3>
          <p className="mt-1.5 text-sm text-ink-600">Import Tally data for this entity.</p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 text-left font-medium">Group</th>
                <th className="px-5 py-3 text-left font-medium">Type</th>
                <th className="px-5 py-3 text-right font-medium">Debit</th>
                <th className="px-5 py-3 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data.rows.map((r) => (
                <tr key={r.name} className="hover:bg-ink-50/60">
                  <td className="px-5 py-2.5 font-medium text-ink-900">{r.name} <span className="text-xs text-ink-400">· {r.count}</span></td>
                  <td className="px-5 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${NATURE[r.nature] ?? NATURE.unknown}`}>{r.nature}</span></td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(r.debit)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(r.credit)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
                <td className="px-5 py-3" colSpan={2}>Total</td>
                <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.debit)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.credit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
